import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth, API } from '@/App';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Upload, CheckCircle, XCircle, AlertTriangle, FileText, TrendingUp,
  Edit3, Save, Plus, Trash2, ExternalLink, Clock, ArrowUpDown, RefreshCw,
  LayoutDashboard, Users, Briefcase, Workflow, Home, Gauge, Activity,
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const SEVERITY_COLORS = {
  normal: 'text-emerald-400 bg-emerald-900/20',
  borderline: 'text-amber-400 bg-amber-900/20',
  abnormal: 'text-orange-400 bg-orange-900/20',
  critical: 'text-red-400 bg-red-900/20',
  unknown: 'text-white/30 bg-white/5',
};

export default function LabIntelligenceV2() {
  const { user } = useAuth();
  // Doctor has read/review-only access — write actions are hidden in UI
  // and additionally enforced server-side. See routes/lab_reports_routes.py.
  const canWrite = ['admin', 'counselor', 'health_coach'].includes(user?.role);
  const isDoctor = user?.role === 'doctor';
  const [subscribers, setSubscribers] = useState([]);
  const [selectedSub, setSelectedSub] = useState('');
  const [tab, setTab] = useState('overview');
  const [reports, setReports] = useState([]);
  const [overview, setOverview] = useState(null);
  const [reviewReport, setReviewReport] = useState(null);
  const [editMarkers, setEditMarkers] = useState([]);
  const [markerHistory, setMarkerHistory] = useState([]);
  const [comparison, setComparison] = useState(null);
  const [queue, setQueue] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [retryingId, setRetryingId] = useState(null);
  const [intelLoading, setIntelLoading] = useState(false);
  const [intelOutput, setIntelOutput] = useState(null);
  const [uploadForm, setUploadForm] = useState({ report_date: new Date().toISOString().split('T')[0], source_lab: '', date_touched: false });
  const [reportDateSuggestion, setReportDateSuggestion] = useState(null);
  const [reviewSummary, setReviewSummary] = useState(null);
  const [reviewMeta, setReviewMeta] = useState(null);
  const [retryMenu, setRetryMenu] = useState(null);  // { reportId, strategies, last_strategy, last_family, ... }
  const [reopenModal, setReopenModal] = useState(null); // { reportId, reason }
  const isAdmin = user?.role === 'admin';
  // Review filters
  const [filterMode, setFilterMode] = useState('all');
  const [filterCategory, setFilterCategory] = useState('');
  const [filterSearch, setFilterSearch] = useState('');

  const params = new URLSearchParams(window.location.search);
  const preselectedSub = params.get('subscriber');
  const preselectedTab = params.get('tab');

  useEffect(() => {
    axios.get(`${API}/users?role=subscriber`).then(r => {
      setSubscribers(r.data || []);
      if (preselectedSub) setSelectedSub(preselectedSub);
    }).catch(() => {});
    loadQueue();
    if (preselectedTab && ['overview','reports','validation','history','comparison','intelligence'].includes(preselectedTab)) {
      setTab(preselectedTab);
    }
  }, []);

  useEffect(() => {
    if (selectedSub) {
      loadReports();
      loadHistory();
      loadComparison();
      loadOverview();
    }
  }, [selectedSub]); // eslint-disable-line

  const loadReports = async () => {
    try { const r = await axios.get(`${API}/lab-v2/reports/${selectedSub}`); setReports(r.data.reports || []); } catch {}
  };
  const loadHistory = async () => {
    try { const r = await axios.get(`${API}/lab-v2/markers/${selectedSub}/history`); setMarkerHistory(r.data.markers || []); } catch {}
  };
  const loadComparison = async () => {
    try { const r = await axios.get(`${API}/lab-v2/markers/${selectedSub}/comparison`); setComparison(r.data); } catch {}
  };
  const loadQueue = async () => {
    try { const r = await axios.get(`${API}/lab-v2/validation-queue`); setQueue(r.data.queue || []); } catch {}
  };
  const loadOverview = async () => {
    if (!selectedSub) return;
    try { const r = await axios.get(`${API}/lab-v2/overview/${selectedSub}`); setOverview(r.data); } catch {}
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !selectedSub) return;
    setUploading(true);
    const fd = new FormData();
    fd.append('file', file);
    fd.append('report_date', uploadForm.report_date);
    fd.append('source_lab', uploadForm.source_lab);
    try {
      const r = await axios.post(`${API}/lab-v2/reports/${selectedSub}/upload`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      const src = r.data.extraction_source || 'manual';
      const conf = r.data.extraction_confidence ?? 0;
      if (r.data.marker_count > 0) {
        toast.success(`${r.data.marker_count} markers extracted via ${src} (confidence ${conf})`);
      } else {
        // Surface any diagnostic notes from the extraction pipeline
        const hint = (r.data.notes && r.data.notes.length > 0)
          ? r.data.notes[0]
          : 'Try Retry Extraction with a different strategy or verify the file format.';
        toast.warning(`Uploaded, but 0 markers extracted. ${hint}`, { duration: 10000 });
      }
      // Hardening pass — if the parser detected a row-date and user hasn't touched the field, surface suggestion
      const suggested = r.data.suggested_report_date;
      if (suggested && suggested !== uploadForm.report_date) {
        setReportDateSuggestion({ suggested, filename: file.name, source: r.data.parser_family });
      }
      loadReports(); loadQueue();
    } catch (err) { toast.error(err.response?.data?.detail || 'Upload failed'); }
    finally { setUploading(false); e.target.value = ''; }
  };

  const applyDateSuggestion = () => {
    if (!reportDateSuggestion) return;
    setUploadForm(f => ({ ...f, report_date: reportDateSuggestion.suggested, date_touched: true }));
    setReportDateSuggestion(null);
    toast.success(`Report date updated to ${reportDateSuggestion.suggested}`);
  };

  const openReview = async (reportId) => {
    if (!selectedSub) { toast.error('Select a subscriber first'); return; }
    try {
      const r = await axios.get(`${API}/lab-v2/reports/${selectedSub}/${reportId}/review`);
      setReviewReport(r.data.report);
      setEditMarkers(JSON.parse(JSON.stringify(r.data.markers || [])));
      setReviewSummary(r.data.extraction_summary || null);
      setReviewMeta({
        parser_family: r.data.parser_family,
        extraction_strategy: r.data.extraction_strategy,
        layout_type: r.data.layout_type,
        extraction_confidence: r.data.extraction_confidence,
        extraction_confidence_band: r.data.extraction_confidence_band,
        route_scores: r.data.route_scores,
      });
      // Reset filters for the new report
      setFilterMode('all'); setFilterCategory(''); setFilterSearch('');
      setTab('validation');
    } catch { toast.error('Failed to load report'); }
  };

  const retryExtraction = async (reportId, strategy = null) => {
    if (!selectedSub) return;
    setRetryingId(reportId);
    try {
      const payload = strategy ? { strategy } : {};
      const r = await axios.post(`${API}/lab-v2/reports/${selectedSub}/${reportId}/retry-extraction`, payload);
      const used = r.data?.parser_family || r.data?.extraction_strategy || r.data?.extraction_source;
      if (r.data?.success) {
        toast.success(`${r.data.message || 'Extraction retried'}${used ? ` · via ${used}` : ''}`);
      } else {
        toast.warning(r.data?.message || 'Retry completed — 0 markers extracted');
      }
      loadReports();
      loadQueue();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Retry failed');
    } finally {
      setRetryingId(null);
    }
  };

  const openRetryMenu = async (reportId) => {
    // Lazy-load retry strategies + last strategy
    try {
      const r = await axios.get(`${API}/lab-v2/reports/${selectedSub}/${reportId}/retry-options`);
      setRetryMenu({ reportId, ...r.data });
    } catch (err) {
      toast.error('Could not load retry options');
    }
  };

  const runIntelligence = async () => {
    if (!selectedSub) return;
    setIntelLoading(true);
    try {
      // try loading latest first
      const latest = await axios.get(`${API}/regenflo-intelligence/latest/${selectedSub}?mode=lab_intelligence`).catch(() => ({ data: {} }));
      if (latest.data?.run?.output_json) {
        setIntelOutput(latest.data.run.output_json);
      } else {
        const r = await axios.post(`${API}/regenflo-intelligence/lab-intelligence/${selectedSub}`);
        setIntelOutput(r.data.output);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Intelligence run failed');
    } finally {
      setIntelLoading(false);
    }
  };

  const recomputeIntelligence = async () => {
    if (!selectedSub) return;
    setIntelLoading(true);
    try {
      const r = await axios.post(`${API}/regenflo-intelligence/lab-intelligence/${selectedSub}`);
      setIntelOutput(r.data.output);
      toast.success('Re-analyzed with current validated labs');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Recompute failed');
    } finally {
      setIntelLoading(false);
    }
  };

  useEffect(() => {
    if (tab === 'intelligence' && selectedSub && !intelOutput && !intelLoading) {
      runIntelligence();
    }
  }, [tab, selectedSub]); // eslint-disable-line

  const handleValidate = async (action) => {
    if (!reviewReport) return;
    try {
      await axios.post(`${API}/lab-v2/reports/${selectedSub}/${reviewReport.id}/validate`, {
        action, markers: editMarkers,
      });
      toast.success(action === 'validate' ? 'Report validated' : action === 'reject' ? 'Report rejected' : 'Draft saved');
      setReviewReport(null);
      loadReports(); loadHistory(); loadComparison(); loadQueue(); loadOverview();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const submitReopen = async () => {
    if (!reopenModal) return;
    const reason = (reopenModal.reason || '').trim();
    if (!reason) {
      toast.error('Reopen reason is required');
      return;
    }
    try {
      await axios.post(
        `${API}/lab-v2/reports/${selectedSub}/${reopenModal.reportId}/reopen`,
        { reason },
      );
      toast.success('Report reopened — back in review queue');
      setReopenModal(null);
      loadReports(); loadQueue(); loadOverview();
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : (detail?.message || 'Failed to reopen'));
    }
  };

  const updateMarker = (idx, field, value) => {
    setEditMarkers(prev => {
      const copy = [...prev];
      copy[idx] = { ...copy[idx], [field]: field === 'value' || field === 'reference_min' || field === 'reference_max' ? (value === '' ? null : parseFloat(value)) : value };
      return copy;
    });
  };

  const removeMarker = (idx) => setEditMarkers(prev => prev.filter((_, i) => i !== idx));
  const addMarker = () => setEditMarkers(prev => [...prev, { marker_id: `mk_new_${Date.now()}`, marker_name_raw: '', value: null, unit: '', reference_min: null, reference_max: null, severity: '', source: 'manual_entry' }]);

  return (
    <div className="min-h-screen bg-[#080C14] text-white p-6">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Admin nav header — keeps Lab Intelligence in the Care OS shell */}
        <nav data-testid="lab-admin-nav" className="flex items-center justify-between border-b border-white/[0.05] pb-3">
          <div className="flex items-center gap-1 text-[11px]">
            <AdminNavLink to="/admin" icon={Home} label="Dashboard" testid="nav-dashboard" />
            <AdminNavLink to="/admin/crm" icon={Users} label="CRM" testid="nav-crm" />
            <AdminNavLink to="/admin/care-ops" icon={Briefcase} label="Care Ops" testid="nav-care-ops" />
            <AdminNavLink to="/admin/lab-intelligence" icon={Activity} label="Lab Intelligence" testid="nav-lab" active />
            {selectedSub && (
              <AdminNavLink to={`/admin/decode-wizard?subscriber_id=${selectedSub}`}
                icon={Workflow} label="DECODE Workspace" testid="nav-decode" />
            )}
          </div>
          <div className="text-[10px] text-white/20">
            {user?.name ? `Signed in as ${user.name}` : ''}
          </div>
        </nav>

        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-semibold text-[#C89B7B]">Lab Intelligence</h1>
            <p className="text-xs text-white/30 mt-0.5">Upload, review, validate, and track biological markers</p>
          </div>
          {queue.length > 0 && (
            <Badge className="bg-amber-800/30 text-amber-300 text-xs">
              <Clock className="w-3 h-3 mr-1" /> {queue.length} pending validation
            </Badge>
          )}
        </div>

        {/* Subscriber selector */}
        <div className="flex gap-3 items-end">
          <div className="w-72">
            <label className="text-[9px] text-white/30 block mb-1">Subscriber</label>
            <Select value={selectedSub} onValueChange={setSelectedSub}>
              <SelectTrigger className="bg-white/[0.03] border-white/[0.08] text-white text-xs">
                <SelectValue placeholder="Select subscriber" />
              </SelectTrigger>
              <SelectContent className="bg-[#1a1f2e] border-white/10 max-h-60">
                {subscribers.map(s => (
                  <SelectItem key={s.id} value={s.id} className="text-white text-xs">{s.name} ({s.email})</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {selectedSub && canWrite && (
            <>
              <div className="w-36">
                <label className="text-[9px] text-white/30 block mb-1">Report Date</label>
                <Input type="date" value={uploadForm.report_date}
                  onChange={e => setUploadForm(f => ({ ...f, report_date: e.target.value, date_touched: true }))}
                  className="bg-white/[0.03] border-white/[0.08] text-white text-xs h-9" />
              </div>
              <div className="w-40">
                <label className="text-[9px] text-white/30 block mb-1">Source Lab</label>
                <Input value={uploadForm.source_lab} onChange={e => setUploadForm(f => ({ ...f, source_lab: e.target.value }))}
                  placeholder="e.g., SRL Diagnostics" className="bg-white/[0.03] border-white/[0.08] text-white text-xs h-9" />
              </div>
              <label className="h-9 px-4 text-xs bg-[#4E7A6B] hover:bg-[#3D695B] text-white rounded-md flex items-center gap-2 cursor-pointer">
                <Upload className="w-3.5 h-3.5" /> {uploading ? 'Uploading...' : 'Upload Report'}
                <input type="file" accept=".csv,.pdf,.png,.jpg,.jpeg,.webp,.heic,.heif" className="hidden" data-testid="lab-upload-input" onChange={handleUpload} disabled={uploading} />
              </label>
            </>
          )}
        </div>

        {selectedSub && reportDateSuggestion && (
          <div data-testid="date-suggestion-banner" className="flex items-center justify-between gap-3 px-4 py-2 rounded-md border border-cyan-700/30 bg-cyan-900/10 text-[11px] text-cyan-200">
            <span>
              Parser detected a report date in this file:&nbsp;
              <strong className="text-cyan-100">{reportDateSuggestion.suggested}</strong>
              <span className="text-cyan-200/40"> · currently set to {uploadForm.report_date}</span>
            </span>
            <div className="flex gap-1.5">
              <Button size="sm" variant="ghost" className="h-6 px-2 text-[10px] text-cyan-200 hover:bg-cyan-800/20"
                onClick={applyDateSuggestion} data-testid="apply-date-suggestion-btn">
                Use detected date
              </Button>
              <Button size="sm" variant="ghost" className="h-6 px-2 text-[10px] text-white/30 hover:bg-white/5"
                onClick={() => setReportDateSuggestion(null)}>
                Dismiss
              </Button>
            </div>
          </div>
        )}

        {selectedSub && (
          <Tabs value={tab} onValueChange={setTab}>
            <TabsList className="bg-white/[0.03] border border-white/[0.06]">
              <TabsTrigger value="overview" className="text-xs data-[state=active]:bg-white/10" data-testid="overview-tab-trigger">Overview</TabsTrigger>
              <TabsTrigger value="reports" className="text-xs data-[state=active]:bg-white/10">Reports</TabsTrigger>
              <TabsTrigger value="validation" className="text-xs data-[state=active]:bg-white/10">
                Validation {queue.filter(q => q.subscriber_id === selectedSub).length > 0 && <span className="ml-1 text-amber-400">*</span>}
              </TabsTrigger>
              <TabsTrigger value="history" className="text-xs data-[state=active]:bg-white/10">Marker History</TabsTrigger>
              <TabsTrigger value="comparison" className="text-xs data-[state=active]:bg-white/10">Comparison</TabsTrigger>
              <TabsTrigger value="intelligence" className="text-xs data-[state=active]:bg-white/10" data-testid="intelligence-tab-trigger">
                ReGenflō Intelligence
              </TabsTrigger>
            </TabsList>

            {/* REPORTS TAB */}
            {/* OVERVIEW TAB */}
            <TabsContent value="overview" className="mt-4" data-testid="overview-panel">
              <OverviewPanel
                overview={overview}
                selectedSub={selectedSub}
                reloadOverview={loadOverview}
                openReview={openReview}
                setTab={setTab}
                runIntelligence={runIntelligence}
                intelLoading={intelLoading}
              />
            </TabsContent>

            <TabsContent value="reports" className="space-y-3 mt-4">
              {reports.length === 0 ? (
                <Card className="bg-white/[0.02] border-white/[0.06]"><CardContent className="p-8 text-center text-white/30 text-sm">No lab reports yet. Upload a CSV or PDF above.</CardContent></Card>
              ) : reports.map(r => (
                <Card key={r.id} className="bg-white/[0.02] border-white/[0.06]">
                  <CardContent className="p-4 flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <FileText className="w-5 h-5 text-white/20" />
                      <div>
                        <p className="text-sm text-white/80">{r.report_date} — {r.source_lab || 'Unknown Lab'}</p>
                        <p className="text-[10px] text-white/30 leading-relaxed">
                          {r.original_filename} | {r.marker_count || 0} markers
                          {r.parser_family && r.parser_family !== 'unknown' && (
                            <span className="ml-1.5 text-[#C89B7B]/70" data-testid={`parser-family-${r.id}`}>
                              · {r.parser_family}
                            </span>
                          )}
                          {r.extraction_confidence != null && r.extraction_confidence > 0 && (
                            <span className={`ml-1.5 ${r.extraction_confidence >= 0.85 ? 'text-emerald-400/70' : r.extraction_confidence >= 0.6 ? 'text-amber-400/70' : 'text-red-400/70'}`}>
                              · conf {r.extraction_confidence}
                              {r.extraction_confidence_band && ` (${r.extraction_confidence_band})`}
                            </span>
                          )}
                          {r.vendor_hint && <span className="ml-1.5 text-white/40">· {r.vendor_hint}</span>}
                          {r.extraction_summary?.unmatched_count > 0 && (
                            <span className="ml-1.5 text-amber-400/50" data-testid={`unmatched-hint-${r.id}`}>
                              · {r.extraction_summary.unmatched_count} unmatched
                            </span>
                          )}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge data-testid={`report-status-${r.id}`} className={`text-[8px] ${
                        r.validation_status === 'validated' ? 'bg-emerald-800/30 text-emerald-300' :
                        r.validation_status === 'rejected' ? 'bg-red-800/30 text-red-300' :
                        r.validation_status === 'reopened' ? 'bg-amber-700/30 text-amber-200 border border-amber-500/30' :
                        r.validation_status === 'draft' ? 'bg-blue-800/30 text-blue-300' :
                        'bg-amber-800/30 text-amber-300'
                      }`}>
                        {r.validation_status || 'pending'}
                      </Badge>
                      {isAdmin && r.validation_status === 'validated' && (
                        <Button
                          data-testid={`reopen-btn-${r.id}`}
                          size="sm"
                          variant="outline"
                          className="h-7 px-2.5 text-[9px] border-amber-700/40 text-amber-300 hover:bg-amber-900/20"
                          onClick={() => setReopenModal({ reportId: r.id, reason: '' })}
                          title="Move this validated report back to the review queue (admin only)"
                        >
                          <RefreshCw className="w-3 h-3 mr-1" />
                          Reopen
                        </Button>
                      )}
                      {(r.validation_status === 'pending' || r.validation_status === 'draft' || r.validation_status === 'reopened' || !r.validation_status) && (
                        <div className="relative">
                          <Button
                            data-testid={`retry-btn-${r.id}`}
                            size="sm"
                            variant="outline"
                            className="h-7 px-2.5 text-[9px] border-amber-700/30 text-amber-300 hover:bg-amber-900/20"
                            disabled={retryingId === r.id}
                            onClick={() => openRetryMenu(r.id)}
                            title="Re-run extraction with the same or a different parser strategy"
                          >
                            <RefreshCw className={`w-3 h-3 mr-1 ${retryingId === r.id ? 'animate-spin' : ''}`} />
                            {retryingId === r.id ? 'Retrying…' : 'Retry'}
                          </Button>
                          {retryMenu && retryMenu.reportId === r.id && (
                            <div data-testid={`retry-menu-${r.id}`} className="absolute right-0 top-8 z-30 w-64 rounded-md border border-white/10 bg-[#10141d] shadow-2xl p-2">
                              <div className="text-[9px] text-white/30 px-2 py-1">
                                Last: <span className="text-[#C89B7B]/70">{retryMenu.last_strategy || retryMenu.last_family || '—'}</span>
                              </div>
                              {(retryMenu.strategies || []).map(s => (
                                <button
                                  key={s}
                                  onClick={() => { setRetryMenu(null); retryExtraction(r.id, s === 'digital' ? null : s); }}
                                  data-testid={`retry-strategy-${s}`}
                                  className="w-full text-left text-[10px] text-white/70 hover:bg-white/[0.05] rounded px-2 py-1.5"
                                >
                                  {s === retryMenu.last_strategy ? '↻ ' : '→ '}{s}
                                  {s === retryMenu.last_strategy && <span className="text-white/30 ml-1">(same again)</span>}
                                </button>
                              ))}
                              <button
                                onClick={() => setRetryMenu(null)}
                                className="w-full text-left text-[9px] text-white/30 hover:bg-white/[0.03] rounded px-2 py-1 mt-1 border-t border-white/5"
                              >Cancel</button>
                            </div>
                          )}
                        </div>
                      )}
                      <Button data-testid={`review-btn-${r.id}`} size="sm" variant="outline" className="h-7 px-2.5 text-[9px] border-white/10 text-white/50"
                        onClick={() => openReview(r.id)}>
                        <Edit3 className="w-3 h-3 mr-1" /> Review
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </TabsContent>

            {/* VALIDATION TAB (Review Panel) */}
            <TabsContent value="validation" className="mt-4">
              {reviewReport ? (
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-sm text-white/80">Reviewing: {reviewReport.report_date} — {reviewReport.source_lab || 'Unknown'}</h3>
                      <p className="text-[10px] text-white/30">
                        {reviewReport.original_filename} | {editMarkers.length} markers
                        {reviewMeta?.parser_family && (
                          <span className="ml-2 text-[#C89B7B]/60" data-testid="review-parser-family">· {reviewMeta.parser_family}</span>
                        )}
                        {reviewMeta?.layout_type && reviewMeta.layout_type !== 'unknown' && (
                          <span className="ml-1.5 text-white/30">· {reviewMeta.layout_type}</span>
                        )}
                        {reviewMeta?.extraction_confidence != null && (
                          <span className={`ml-1.5 ${reviewMeta.extraction_confidence >= 0.85 ? 'text-emerald-400/60' : reviewMeta.extraction_confidence >= 0.6 ? 'text-amber-400/60' : 'text-red-400/70'}`} data-testid="review-confidence">
                            · conf {reviewMeta.extraction_confidence} {reviewMeta.extraction_confidence_band && `(${reviewMeta.extraction_confidence_band})`}
                          </span>
                        )}
                      </p>
                      {reviewMeta?.extraction_confidence != null && reviewMeta.extraction_confidence < 0.6 && (
                        <div data-testid="low-confidence-warning" className="mt-1 text-[10px] text-red-300 flex items-center gap-1.5">
                          <AlertTriangle className="w-3 h-3" />
                          Low-confidence extraction. Please review carefully before validating — consider retrying with a different parser strategy.
                        </div>
                      )}
                    </div>
                    <div className="flex gap-2" data-testid={canWrite ? 'lab-write-actions' : 'lab-readonly-actions'}>
                      {canWrite ? (
                        <>
                          <Button data-testid="validate-btn" size="sm" className="h-7 px-3 text-[9px] bg-emerald-700/30 text-emerald-300" onClick={() => handleValidate('validate')}>
                            <CheckCircle className="w-3 h-3 mr-1" /> Validate
                          </Button>
                          <Button size="sm" variant="outline" className="h-7 px-3 text-[9px] border-blue-700/20 text-blue-400" onClick={() => handleValidate('save_draft')}>
                            <Save className="w-3 h-3 mr-1" /> Save Draft
                          </Button>
                          <Button size="sm" variant="outline" className="h-7 px-3 text-[9px] border-red-700/20 text-red-400" onClick={() => handleValidate('reject')}>
                            <XCircle className="w-3 h-3 mr-1" /> Reject
                          </Button>
                        </>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-blue-500/10 text-blue-300 text-[10px]" data-testid="doctor-readonly-pill">
                          Read &amp; review only
                        </span>
                      )}
                      <Button size="sm" variant="ghost" className="h-7 px-2 text-[9px] text-white/30" onClick={() => setReviewReport(null)}>Close</Button>
                    </div>
                  </div>

                  {/* Extraction Summary Card (Hardening Pass) */}
                  {reviewSummary && (
                    <div data-testid="extraction-summary-card" className="grid grid-cols-2 md:grid-cols-6 gap-2 p-3 rounded-md border border-white/[0.06] bg-white/[0.02]">
                      <SummaryStat label="Total" value={reviewSummary.total_markers} tone="neutral" />
                      <SummaryStat label="Library Matched" value={reviewSummary.library_matched_count} tone="positive" />
                      <SummaryStat label="Unmatched" value={reviewSummary.unmatched_count} tone={reviewSummary.unmatched_count > 0 ? 'warn' : 'neutral'} />
                      <SummaryStat label="Low Conf." value={reviewSummary.low_confidence_count} tone={reviewSummary.low_confidence_count > 0 ? 'warn' : 'neutral'} />
                      <SummaryStat label="Invalid Range" value={reviewSummary.invalid_range_count} tone={reviewSummary.invalid_range_count > 0 ? 'danger' : 'neutral'} />
                      <SummaryStat label="Qualitative" value={reviewSummary.qualitative_count + reviewSummary.manual_review_count} tone="info" />
                    </div>
                  )}

                  {/* Filter Toolbar (Hardening Pass) */}
                  <div data-testid="review-filter-toolbar" className="flex flex-wrap items-center gap-2 p-2 rounded-md border border-white/[0.04] bg-white/[0.01]">
                    <FilterChip label="All" value="all" active={filterMode === 'all'} onClick={setFilterMode} count={editMarkers.length} />
                    <FilterChip label="Unmatched" value="unmatched" active={filterMode === 'unmatched'} onClick={setFilterMode}
                      count={editMarkers.filter(m => !m.library_matched).length} />
                    <FilterChip label="Low Confidence" value="low_conf" active={filterMode === 'low_conf'} onClick={setFilterMode}
                      count={editMarkers.filter(m => (m.confidence || 0) < 0.7).length} />
                    <FilterChip label="Invalid Range" value="invalid_range" active={filterMode === 'invalid_range'} onClick={setFilterMode}
                      count={editMarkers.filter(m => m.source_range_invalid || m.needs_manual_range_review).length} />
                    <FilterChip label="Qualitative" value="qualitative" active={filterMode === 'qualitative'} onClick={setFilterMode}
                      count={editMarkers.filter(m => ['qualitative', 'manual_review', 'narrative'].includes(m.row_type)).length} />
                    <FilterChip label="Abnormal" value="abnormal" active={filterMode === 'abnormal'} onClick={setFilterMode}
                      count={editMarkers.filter(m => ['high', 'low', 'abnormal', 'critical'].includes(m.severity)).length} />
                    <Select value={filterCategory || 'all'} onValueChange={v => setFilterCategory(v === 'all' ? '' : v)}>
                      <SelectTrigger className="h-7 w-36 text-[10px] bg-white/[0.02] border-white/[0.08] text-white/70">
                        <SelectValue placeholder="Category" />
                      </SelectTrigger>
                      <SelectContent className="bg-[#1a1f2e] border-white/10 text-[10px]">
                        <SelectItem value="all" className="text-white/70">All categories</SelectItem>
                        {[...new Set(editMarkers.map(m => m.category).filter(Boolean))].sort().map(c => (
                          <SelectItem key={c} value={c} className="text-white/70">{c}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Input data-testid="marker-search-input" value={filterSearch} onChange={e => setFilterSearch(e.target.value)}
                      placeholder="Search marker…" className="h-7 w-48 text-[10px] bg-white/[0.02] border-white/[0.08] text-white/70" />
                    {(filterMode !== 'all' || filterCategory || filterSearch) && (
                      <Button size="sm" variant="ghost" className="h-7 px-2 text-[10px] text-white/30"
                        onClick={() => { setFilterMode('all'); setFilterCategory(''); setFilterSearch(''); }}>Reset</Button>
                    )}
                  </div>
                  {reviewReport.original_file_url && (
                    <a href={reviewReport.original_file_url} target="_blank" rel="noreferrer" className="text-[10px] text-cyan-400 flex items-center gap-1">
                      <ExternalLink className="w-3 h-3" /> View Original File
                    </a>
                  )}
                  {/* Editable marker table */}
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="border-b border-white/[0.06] text-white/30 text-[9px]">
                          <th className="py-2 text-left pl-2 w-6">#</th>
                          <th className="py-2 text-left">Marker Name</th>
                          <th className="py-2 text-left w-20">Value</th>
                          <th className="py-2 text-left w-16">Unit</th>
                          <th className="py-2 text-left w-16">Ref Min</th>
                          <th className="py-2 text-left w-16">Ref Max</th>
                          <th className="py-2 text-left w-20">Severity</th>
                          <th className="py-2 text-left w-36">Flags</th>
                          <th className="py-2 w-8"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredMarkerRows(editMarkers, filterMode, filterCategory, filterSearch).map(({ m, idx }) => (
                          <tr key={m.marker_id || idx} data-testid={`marker-row-${idx}`}
                            className={`border-b border-white/[0.03] hover:bg-white/[0.02] ${rowHighlightClass(m)}`}>
                            <td className="py-1.5 pl-2 text-white/20">{idx + 1}</td>
                            <td className="py-1.5">
                              <Input value={m.marker_name_raw || m.marker_name_normalized || m.name || ''} onChange={e => updateMarker(idx, 'marker_name_raw', e.target.value)}
                                className="bg-transparent border-white/[0.06] text-white text-xs h-7 w-full" />
                              {m.library_matched && m.marker_name_normalized && m.marker_name_normalized !== m.marker_name_raw && (
                                <span className="text-[8px] text-emerald-400/50 pl-1">→ {m.marker_name_normalized}</span>
                              )}
                            </td>
                            <td className="py-1.5"><Input type="number" step="any" value={m.value ?? ''} onChange={e => updateMarker(idx, 'value', e.target.value)}
                              className="bg-transparent border-white/[0.06] text-white text-xs h-7 w-20" /></td>
                            <td className="py-1.5"><Input value={m.unit || ''} onChange={e => updateMarker(idx, 'unit', e.target.value)}
                              className="bg-transparent border-white/[0.06] text-white text-xs h-7 w-16" /></td>
                            <td className="py-1.5"><Input type="number" step="any" value={m.reference_min ?? ''} onChange={e => updateMarker(idx, 'reference_min', e.target.value)}
                              className="bg-transparent border-white/[0.06] text-white text-xs h-7 w-16" /></td>
                            <td className="py-1.5"><Input type="number" step="any" value={m.reference_max ?? ''} onChange={e => updateMarker(idx, 'reference_max', e.target.value)}
                              className="bg-transparent border-white/[0.06] text-white text-xs h-7 w-16" /></td>
                            <td className="py-1.5">
                              <Badge className={`text-[7px] ${SEVERITY_COLORS[m.severity] || SEVERITY_COLORS.unknown}`}>{m.severity || '?'}</Badge>
                            </td>
                            <td className="py-1.5">
                              <RowFlagBadges m={m} />
                            </td>
                            <td className="py-1.5"><button onClick={() => removeMarker(idx)} className="text-red-400/40 hover:text-red-400"><Trash2 className="w-3 h-3" /></button></td>
                          </tr>
                        ))}
                        {filteredMarkerRows(editMarkers, filterMode, filterCategory, filterSearch).length === 0 && (
                          <tr><td colSpan={9} className="py-8 text-center text-white/30 text-[11px]">No markers match the current filters. <button className="text-cyan-400 underline" onClick={() => { setFilterMode('all'); setFilterCategory(''); setFilterSearch(''); }}>Reset filters</button></td></tr>
                        )}
                      </tbody>
                    </table>
                    <Button size="sm" variant="ghost" className="mt-2 text-[9px] text-white/30" onClick={addMarker}>
                      <Plus className="w-3 h-3 mr-1" /> Add Marker
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="space-y-3">
                  <p className="text-xs text-white/30">Select a report from the Reports tab and click "Review" to validate markers.</p>
                  {queue.filter(q => q.subscriber_id === selectedSub).length > 0 && (
                    <div className="space-y-2">
                      <p className="text-xs text-amber-400/60">Pending validation for this subscriber:</p>
                      {queue.filter(q => q.subscriber_id === selectedSub).map(q => (
                        <Card key={q.id} className="bg-amber-900/5 border-amber-800/15">
                          <CardContent className="p-3 flex items-center justify-between">
                            <span className="text-xs text-white/60">{q.report_date} — {q.marker_count} markers</span>
                            <Button size="sm" variant="outline" className="h-6 px-2 text-[8px]" onClick={() => { openReview(q.id); }}>Review</Button>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </TabsContent>

            {/* MARKER HISTORY TAB */}
            <TabsContent value="history" className="mt-4">
              {markerHistory.length === 0 ? (
                <Card className="bg-white/[0.02] border-white/[0.06]"><CardContent className="p-8 text-center text-white/30 text-sm">No validated marker data yet.</CardContent></Card>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="border-b border-white/[0.06] text-white/30 text-[9px]">
                        <th className="py-2 text-left">Marker</th>
                        <th className="py-2 text-left">Axis</th>
                        <th className="py-2 text-left">Latest</th>
                        <th className="py-2 text-left">Previous</th>
                        <th className="py-2 text-left">Unit</th>
                        <th className="py-2 text-left">Trend</th>
                        <th className="py-2 text-left">Data Points</th>
                      </tr>
                    </thead>
                    <tbody>
                      {markerHistory.map((m, i) => (
                        <tr key={i} className="border-b border-white/[0.03]">
                          <td className="py-2 text-white/70">{m.name}</td>
                          <td className="py-2"><Badge className="text-[7px] bg-white/5 text-white/40">{m.axis || '-'}</Badge></td>
                          <td className="py-2">
                            <span className={m.latest?.severity === 'normal' ? 'text-emerald-400' : m.latest?.severity === 'abnormal' ? 'text-orange-400' : m.latest?.severity === 'critical' ? 'text-red-400' : 'text-white/60'}>
                              {m.latest?.value ?? '-'}
                            </span>
                          </td>
                          <td className="py-2 text-white/40">{m.previous?.value ?? '-'}</td>
                          <td className="py-2 text-white/30">{m.unit}</td>
                          <td className="py-2">
                            <Badge className={`text-[7px] ${m.trend === 'improving' ? 'bg-emerald-900/20 text-emerald-400' : m.trend === 'worsening' ? 'bg-red-900/20 text-red-400' : 'bg-white/5 text-white/30'}`}>
                              {m.trend} {m.delta_percent != null ? `(${m.delta_percent > 0 ? '+' : ''}${m.delta_percent}%)` : ''}
                            </Badge>
                          </td>
                          <td className="py-2 text-white/30">{m.values?.length || 0}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </TabsContent>

            {/* COMPARISON TAB (PreDECODE/DECODE ready) */}
            <TabsContent value="comparison" className="mt-4">
              {!comparison?.has_data ? (
                <Card className="bg-white/[0.02] border-white/[0.06]"><CardContent className="p-8 text-center text-white/30 text-sm">Need at least one validated report for comparison.</CardContent></Card>
              ) : (
                <div className="space-y-3">
                  <div className="flex gap-4 text-[10px] text-white/30">
                    <span>Latest: <strong className="text-white/60">{comparison.latest_date}</strong></span>
                    {comparison.previous_date && <span>Previous: <strong className="text-white/60">{comparison.previous_date}</strong></span>}
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="border-b border-white/[0.06] text-white/30 text-[9px]">
                          <th className="py-2 text-left">Marker</th>
                          <th className="py-2 text-left">Axis</th>
                          <th className="py-2 text-right">Previous</th>
                          <th className="py-2 text-right">Latest</th>
                          <th className="py-2 text-left">Unit</th>
                          <th className="py-2 text-left">Ref Range</th>
                          <th className="py-2 text-left">Status</th>
                          <th className="py-2 text-left">Trend</th>
                        </tr>
                      </thead>
                      <tbody>
                        {comparison.comparisons.map((c, i) => (
                          <tr key={i} className="border-b border-white/[0.03]">
                            <td className="py-2 text-white/70">{c.marker}</td>
                            <td className="py-2"><Badge className="text-[7px] bg-white/5 text-white/40">{c.axis || '-'}</Badge></td>
                            <td className="py-2 text-right text-white/40">{c.previous_value ?? '-'}</td>
                            <td className="py-2 text-right">
                              <span className={c.latest_severity === 'normal' ? 'text-emerald-400' : c.latest_severity === 'abnormal' ? 'text-orange-400' : c.latest_severity === 'critical' ? 'text-red-400' : 'text-white/60'}>
                                {c.latest_value ?? '-'}
                              </span>
                            </td>
                            <td className="py-2 text-white/30">{c.unit}</td>
                            <td className="py-2 text-white/20">{c.reference_min ?? ''} - {c.reference_max ?? ''}</td>
                            <td className="py-2"><Badge className={`text-[7px] ${SEVERITY_COLORS[c.latest_severity] || SEVERITY_COLORS.unknown}`}>{c.latest_severity}</Badge></td>
                            <td className="py-2">
                              <Badge className={`text-[7px] ${c.trend === 'improving' ? 'bg-emerald-900/20 text-emerald-400' : c.trend === 'worsening' ? 'bg-red-900/20 text-red-400' : c.trend === 'new' ? 'bg-blue-900/20 text-blue-400' : 'bg-white/5 text-white/30'}`}>
                                {c.trend} {c.delta != null ? `(${c.delta > 0 ? '+' : ''}${c.delta})` : ''}
                              </Badge>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </TabsContent>

            {/* REGENFLŌ INTELLIGENCE TAB */}
            <TabsContent value="intelligence" className="mt-4 space-y-4" data-testid="intelligence-tab">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-sm font-medium text-[#C89B7B]">ReGenflō Intelligence Analysis</h3>
                  <p className="text-[10px] text-white/30 mt-0.5">
                    Shared backbone: marker analysis · axis scoring · relationship signals · contradictions · priorities · trajectory
                  </p>
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  disabled={intelLoading}
                  className="h-8 px-3 text-[10px] border-white/10 text-white/70"
                  onClick={recomputeIntelligence}
                  data-testid="intel-recompute-btn"
                >
                  {intelLoading ? 'Analyzing…' : 'Recompute from validated labs'}
                </Button>
              </div>

              {!intelOutput && !intelLoading && (
                <Card className="bg-white/[0.02] border-white/[0.06]">
                  <CardContent className="p-6 text-center text-white/40 text-xs">
                    Click "Recompute from validated labs" to generate analysis.
                  </CardContent>
                </Card>
              )}

              {intelLoading && (
                <Card className="bg-white/[0.02] border-white/[0.06]">
                  <CardContent className="p-6 text-center text-white/40 text-xs">Running engine…</CardContent>
                </Card>
              )}

              {intelOutput && (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  {/* Meta + narrative */}
                  <Card className="bg-white/[0.02] border-white/[0.06] lg:col-span-2">
                    <CardContent className="p-4 space-y-2">
                      <div className="flex items-center gap-3 text-[10px] text-white/30">
                        <span>Engine: {intelOutput.meta?.engine_version}</span>
                        <span>·</span>
                        <span>Confidence: <span className="text-white/70">{intelOutput.meta?.overall_confidence?.label} ({intelOutput.meta?.overall_confidence?.score})</span></span>
                        <span>·</span>
                        <span>Reports used: {intelOutput.input_summary?.lab_report_count}</span>
                        <span>·</span>
                        <span>Markers: {intelOutput.input_summary?.marker_count}</span>
                      </div>
                      <p className="text-xs text-white/80 leading-relaxed" data-testid="intel-clinical-summary">
                        {intelOutput.narrative?.clinical_summary}
                      </p>
                      <p className="text-xs text-[#C89B7B]/80 leading-relaxed italic">
                        {intelOutput.narrative?.regenflo_summary}
                      </p>
                    </CardContent>
                  </Card>

                  {/* Priorities */}
                  <Card className="bg-white/[0.02] border-white/[0.06]">
                    <CardContent className="p-4 space-y-2">
                      <h4 className="text-[11px] uppercase tracking-wider text-white/40">Priorities</h4>
                      {intelOutput.priorities?.length ? intelOutput.priorities.slice(0, 8).map(p => (
                        <div key={p.rank} className="border-l-2 border-[#C89B7B]/40 pl-3 py-1">
                          <div className="flex items-center gap-2 text-[11px]">
                            <span className="text-white/30">#{p.rank}</span>
                            <Badge className={`text-[8px] ${
                              p.type === 'root_driver' ? 'bg-red-800/30 text-red-300' :
                              p.type === 'amplifier' ? 'bg-amber-800/30 text-amber-300' :
                              p.type === 'secondary' ? 'bg-blue-800/30 text-blue-300' :
                              'bg-white/10 text-white/50'
                            }`}>{p.type}</Badge>
                            <span className="text-white/80">{p.label}</span>
                            <span className="text-white/30 ml-auto">{p.severity}</span>
                          </div>
                          {p.recommended_focus && (
                            <p className="text-[10px] text-white/40 mt-0.5">→ {p.recommended_focus}</p>
                          )}
                        </div>
                      )) : <p className="text-[11px] text-white/30">No priorities flagged.</p>}
                    </CardContent>
                  </Card>

                  {/* Axes */}
                  <Card className="bg-white/[0.02] border-white/[0.06]">
                    <CardContent className="p-4 space-y-2">
                      <h4 className="text-[11px] uppercase tracking-wider text-white/40">Axis Analysis</h4>
                      {intelOutput.axis_analysis?.filter(a => a.status !== 'optimal' && a.status !== 'insufficient_data').slice(0, 10).map(a => (
                        <div key={a.axis_key} className="flex items-center gap-3 text-[11px]">
                          <span className={`inline-block w-1.5 h-1.5 rounded-full ${
                            a.status === 'critical' ? 'bg-red-400' :
                            a.status === 'stressed' ? 'bg-amber-400' :
                            a.status === 'suboptimal' ? 'bg-blue-400' : 'bg-emerald-400'
                          }`} />
                          <span className="text-white/70 flex-1">{a.axis_name}</span>
                          <span className="text-white/40">{a.score.toFixed(2)}</span>
                          <Badge className="text-[8px] bg-white/5 text-white/60">{a.status}</Badge>
                        </div>
                      ))}
                    </CardContent>
                  </Card>

                  {/* Relationship signals */}
                  <Card className="bg-white/[0.02] border-white/[0.06]">
                    <CardContent className="p-4 space-y-2">
                      <h4 className="text-[11px] uppercase tracking-wider text-white/40">Relationship Signals</h4>
                      {intelOutput.relationship_signals?.length ? intelOutput.relationship_signals.map(s => (
                        <div key={s.signal_key} className="border-l-2 border-white/10 pl-3 py-1">
                          <p className="text-[11px] text-white/80">{s.signal_name}</p>
                          <p className="text-[10px] text-white/40 mt-0.5">{s.summary}</p>
                        </div>
                      )) : <p className="text-[11px] text-white/30">No relationship signals.</p>}
                    </CardContent>
                  </Card>

                  {/* Contradictions */}
                  <Card className="bg-white/[0.02] border-white/[0.06]">
                    <CardContent className="p-4 space-y-2">
                      <h4 className="text-[11px] uppercase tracking-wider text-white/40">Contradictions / Hidden Risk</h4>
                      {intelOutput.contradictions?.length ? intelOutput.contradictions.map(c => (
                        <div key={c.key} className="border-l-2 border-red-400/30 pl-3 py-1">
                          <p className="text-[11px] text-white/80">{c.label}</p>
                          <p className="text-[10px] text-white/40 mt-0.5">{c.summary}</p>
                        </div>
                      )) : <p className="text-[11px] text-white/30">No contradictions detected.</p>}
                    </CardContent>
                  </Card>

                  {/* Trajectory */}
                  <Card className="bg-white/[0.02] border-white/[0.06] lg:col-span-2">
                    <CardContent className="p-4 flex items-center gap-4">
                      <h4 className="text-[11px] uppercase tracking-wider text-white/40">Trajectory</h4>
                      <Badge className="text-[9px] bg-white/5 text-white/70">{intelOutput.trajectory?.status}</Badge>
                      <span className="text-[10px] text-white/40">{intelOutput.trajectory?.summary}</span>
                    </CardContent>
                  </Card>
                </div>
              )}
            </TabsContent>
          </Tabs>
        )}
      </div>

      {/* Reopen reason modal */}
      {reopenModal && (
        <div
          data-testid="lab-reopen-modal"
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4"
          onClick={(e) => { if (e.target === e.currentTarget) setReopenModal(null); }}
        >
          <div className="w-full max-w-md rounded-lg border border-amber-500/30 bg-[#10141d] p-5">
            <div className="flex items-center gap-2 mb-2">
              <RefreshCw className="w-4 h-4 text-amber-300" />
              <h3 className="text-sm text-white/90">Reopen validated lab report</h3>
            </div>
            <p className="text-[11px] text-white/50 mb-4">
              This will move the report from <span className="text-emerald-300">validated</span> back to <span className="text-amber-300">reopened</span>,
              returning it to the review queue. A reason is required and will be written to the audit log.
            </p>
            <textarea
              data-testid="lab-reopen-reason-input"
              value={reopenModal.reason}
              onChange={(e) => setReopenModal({ ...reopenModal, reason: e.target.value })}
              rows={4}
              placeholder="Reason for reopening (e.g. extraction error, marker entered with wrong unit, new pages received from lab)…"
              className="w-full rounded-md border border-white/10 bg-black/40 text-white/90 text-xs p-3 focus:outline-none focus:border-amber-500/50"
            />
            <div className="flex justify-end gap-2 mt-4">
              <Button
                data-testid="lab-reopen-cancel"
                size="sm"
                variant="outline"
                className="h-7 px-3 text-[10px] border-white/10 text-white/70"
                onClick={() => setReopenModal(null)}
              >
                Cancel
              </Button>
              <Button
                data-testid="lab-reopen-submit"
                size="sm"
                className="h-7 px-3 text-[10px] bg-amber-700/40 text-amber-100 hover:bg-amber-700/60"
                onClick={submitReopen}
              >
                Reopen report
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


// ───────────────── Helper components (Hardening Pass + Phase-2 UX) ─────────────────

function AdminNavLink({ to, icon: Icon, label, testid, active = false }) {
  return (
    <Link
      to={to}
      data-testid={testid}
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md transition-colors ${
        active
          ? 'bg-[#C89B7B]/10 text-[#E8C9A8] border border-[#C89B7B]/20'
          : 'text-white/40 hover:text-white/70 hover:bg-white/[0.03]'
      }`}
    >
      <Icon className="w-3 h-3" />
      {label}
    </Link>
  );
}

function OverviewPanel({ overview, selectedSub, reloadOverview, openReview, setTab, runIntelligence, intelLoading }) {
  if (!overview) {
    return (
      <div className="text-center py-12 text-white/30 text-xs" data-testid="overview-loading">
        Loading overview…
      </div>
    );
  }
  const { counts, latest_upload, latest_validated, quality, category_summary, top_concerns, intelligence_freshness } = overview;
  const freshness = intelligence_freshness || {};
  const freshnessTone = {
    current: 'bg-emerald-800/25 text-emerald-300 border-emerald-700/30',
    stale: 'bg-amber-800/25 text-amber-300 border-amber-700/30',
    no_intelligence: 'bg-white/[0.04] text-white/40 border-white/10',
    no_labs: 'bg-white/[0.04] text-white/40 border-white/10',
  }[freshness.status] || 'bg-white/[0.04] text-white/40 border-white/10';

  const latestPendingId = latest_upload && (latest_upload.validation_status === 'pending' || latest_upload.validation_status === 'draft')
    ? latest_upload.id : null;

  return (
    <div className="space-y-4" data-testid="overview-content">
      {/* Row 1 — counts + quality */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <OverviewStatCard label="Total Reports" value={counts.total} tone="neutral" icon={FileText} testid="overview-total" />
        <OverviewStatCard label="Validated" value={counts.validated} tone="positive" icon={CheckCircle} testid="overview-validated" />
        <OverviewStatCard label="Pending" value={counts.pending + (counts.draft || 0)} tone={counts.pending + (counts.draft || 0) > 0 ? 'warn' : 'neutral'} icon={Clock} testid="overview-pending" />
        <OverviewStatCard label="Rejected" value={counts.rejected} tone={counts.rejected > 0 ? 'danger' : 'neutral'} icon={XCircle} testid="overview-rejected" />
        <OverviewStatCard label="Abnormal Markers" value={quality.total_abnormal} tone={quality.total_abnormal > 0 ? 'warn' : 'neutral'} icon={AlertTriangle} testid="overview-abnormal" />
      </div>

      {/* Row 2 — latest upload + intelligence freshness */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <div className="rounded-md border border-white/[0.06] bg-white/[0.02] p-4">
          <div className="flex items-center justify-between mb-2">
            <div className="text-[10px] uppercase tracking-wider text-white/30">Latest Upload</div>
            <button onClick={reloadOverview} className="text-[9px] text-white/30 hover:text-white/60 flex items-center gap-1" data-testid="refresh-overview-btn">
              <RefreshCw className="w-3 h-3" /> refresh
            </button>
          </div>
          {latest_upload ? (
            <div className="text-[11px] text-white/70 space-y-1">
              <div className="font-medium text-white/90">{latest_upload.report_date} — {latest_upload.source_lab || 'Unknown lab'}</div>
              <div className="text-white/40">
                {latest_upload.original_filename} · {latest_upload.marker_count || 0} markers
              </div>
              <div className="flex flex-wrap gap-1.5 pt-1">
                {latest_upload.parser_family && <Chip tone="info">{latest_upload.parser_family}</Chip>}
                {latest_upload.extraction_source && <Chip tone="neutral">{latest_upload.extraction_source}</Chip>}
                {latest_upload.extraction_confidence != null && (
                  <Chip tone={latest_upload.extraction_confidence >= 0.85 ? 'positive' : latest_upload.extraction_confidence >= 0.6 ? 'warn' : 'danger'}>
                    conf {latest_upload.extraction_confidence}
                    {latest_upload.extraction_confidence_band && ` · ${latest_upload.extraction_confidence_band}`}
                  </Chip>
                )}
                <Chip tone={latest_upload.validation_status === 'validated' ? 'positive' : 'warn'}>
                  {latest_upload.validation_status || 'pending'}
                </Chip>
              </div>
            </div>
          ) : (
            <div className="text-white/30 text-xs">No reports uploaded yet.</div>
          )}
        </div>

        <div className="rounded-md border border-white/[0.06] bg-white/[0.02] p-4">
          <div className="text-[10px] uppercase tracking-wider text-white/30 mb-2">ReGenflō Intelligence</div>
          <div className="flex items-center gap-2 mb-2" data-testid="intelligence-freshness">
            <Gauge className="w-3.5 h-3.5 text-white/40" />
            <span className={`text-[10px] px-2 py-0.5 rounded border ${freshnessTone}`} data-testid={`freshness-${freshness.status}`}>
              {freshness.status ? freshness.status.replace(/_/g, ' ') : 'unknown'}
            </span>
            {freshness.last_refreshed_at && (
              <span className="text-[10px] text-white/30">
                · last refresh {new Date(freshness.last_refreshed_at).toLocaleString()}
              </span>
            )}
          </div>
          {freshness.latest_validated_lab_date && (
            <div className="text-[10px] text-white/40">Latest validated lab: {freshness.latest_validated_lab_date}</div>
          )}
          {freshness.engine_version && (
            <div className="text-[10px] text-white/30">Engine: {freshness.engine_version}</div>
          )}
          <div className="mt-3 flex flex-wrap gap-1.5">
            <Button size="sm" variant="outline" disabled={intelLoading}
              className="h-7 px-2.5 text-[10px] border-[#C89B7B]/30 text-[#C89B7B] hover:bg-[#C89B7B]/10"
              onClick={() => { setTab('intelligence'); runIntelligence(); }}
              data-testid="overview-recompute-intel">
              {intelLoading ? 'Recomputing…' : freshness.status === 'stale' ? 'Recompute (stale)' : 'Recompute Intelligence'}
            </Button>
            {selectedSub && (
              <Link to={`/admin/decode-wizard?subscriber_id=${selectedSub}`}>
                <Button size="sm" variant="outline" className="h-7 px-2.5 text-[10px] border-white/10 text-white/60" data-testid="overview-decode-btn">
                  <Workflow className="w-3 h-3 mr-1" /> DECODE Workspace
                </Button>
              </Link>
            )}
          </div>
        </div>
      </div>

      {/* Row 3 — top concerns + category summary */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <div className="rounded-md border border-white/[0.06] bg-white/[0.02] p-4">
          <div className="text-[10px] uppercase tracking-wider text-white/30 mb-2">Top Concerns (latest validated)</div>
          {top_concerns && top_concerns.length > 0 ? (
            <div className="space-y-1.5" data-testid="overview-top-concerns">
              {top_concerns.map((c, i) => (
                <div key={i} className="flex items-center justify-between text-[11px] py-1 border-b border-white/[0.03]">
                  <span className="text-white/80 truncate mr-2">{c.name}</span>
                  <div className="flex items-center gap-1.5 shrink-0">
                    <span className="text-white/50">{c.value}{c.unit ? ` ${c.unit}` : ''}</span>
                    <Badge className={`text-[8px] ${c.severity === 'high' ? 'bg-red-900/30 text-red-300' : c.severity === 'low' ? 'bg-amber-900/30 text-amber-300' : c.severity === 'critical' ? 'bg-red-900/60 text-red-200' : 'bg-white/10 text-white/60'}`}>
                      {c.severity}
                    </Badge>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-[11px] text-white/30">
              {latest_validated ? 'No abnormal markers in the latest validated report.' : 'Validate a report to see top concerns.'}
            </div>
          )}
        </div>

        <div className="rounded-md border border-white/[0.06] bg-white/[0.02] p-4">
          <div className="text-[10px] uppercase tracking-wider text-white/30 mb-2">By Category (latest validated)</div>
          {category_summary && category_summary.length > 0 ? (
            <div className="space-y-1" data-testid="overview-category-summary">
              {category_summary.map((c, i) => (
                <div key={i} className="flex items-center justify-between text-[11px] py-1">
                  <span className="text-white/70 capitalize">{c.category || 'uncategorized'}</span>
                  <span className="text-white/40">
                    {c.total}
                    {c.abnormal > 0 && (
                      <span className="ml-1 text-amber-400/70">· {c.abnormal} abnormal</span>
                    )}
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-[11px] text-white/30">
              {latest_validated ? 'No category metadata yet.' : 'Validate a report to see a category breakdown.'}
            </div>
          )}
        </div>
      </div>

      {/* Row 4 — quick actions */}
      <div className="flex flex-wrap gap-2 pt-2" data-testid="overview-quick-actions">
        <Button size="sm" variant="outline"
          className="h-8 px-3 text-[11px] border-amber-700/30 text-amber-300 hover:bg-amber-900/20"
          onClick={() => { if (latestPendingId) openReview(latestPendingId); else setTab('reports'); }}
          disabled={!latestPendingId}
          data-testid="overview-review-latest-pending">
          <Edit3 className="w-3.5 h-3.5 mr-1" /> Review latest pending
        </Button>
        <Button size="sm" variant="outline"
          className="h-8 px-3 text-[11px] border-white/10 text-white/70"
          onClick={() => setTab('comparison')}
          data-testid="overview-open-comparison">
          <ArrowUpDown className="w-3.5 h-3.5 mr-1" /> Open latest comparison
        </Button>
        <Button size="sm" variant="outline"
          className="h-8 px-3 text-[11px] border-white/10 text-white/70"
          onClick={() => setTab('reports')}
          data-testid="overview-open-reports">
          <FileText className="w-3.5 h-3.5 mr-1" /> All reports
        </Button>
      </div>
    </div>
  );
}

function OverviewStatCard({ label, value, tone, icon: Icon, testid }) {
  const toneClass = {
    neutral: 'text-white/70',
    positive: 'text-emerald-300',
    warn: 'text-amber-300',
    danger: 'text-red-300',
  }[tone] || 'text-white/70';
  return (
    <div className="rounded-md border border-white/[0.06] bg-white/[0.02] p-3" data-testid={testid}>
      <div className="flex items-center justify-between mb-1">
        <span className="text-[9px] uppercase tracking-wider text-white/30">{label}</span>
        {Icon && <Icon className="w-3 h-3 text-white/25" />}
      </div>
      <div className={`text-2xl font-semibold ${toneClass}`}>{value ?? 0}</div>
    </div>
  );
}

function Chip({ children, tone = 'neutral' }) {
  const toneClass = {
    neutral: 'bg-white/[0.04] text-white/50 border-white/10',
    positive: 'bg-emerald-900/25 text-emerald-300 border-emerald-700/25',
    warn: 'bg-amber-900/25 text-amber-300 border-amber-700/25',
    danger: 'bg-red-900/25 text-red-300 border-red-700/25',
    info: 'bg-cyan-900/25 text-cyan-300 border-cyan-700/25',
  }[tone] || 'bg-white/[0.04] text-white/50 border-white/10';
  return <span className={`inline-block text-[9px] px-1.5 py-[1px] rounded border ${toneClass}`}>{children}</span>;
}

function SummaryStat({ label, value, tone = 'neutral' }) {
  const toneClass = {
    neutral: 'text-white/60',
    positive: 'text-emerald-300',
    warn: 'text-amber-300',
    danger: 'text-red-300',
    info: 'text-cyan-300',
  }[tone] || 'text-white/60';
  return (
    <div className="flex flex-col items-start">
      <span className="text-[9px] uppercase tracking-wider text-white/30">{label}</span>
      <span className={`text-lg font-semibold ${toneClass}`} data-testid={`summary-${label.toLowerCase().replace(/\s+/g, '-')}`}>
        {value ?? 0}
      </span>
    </div>
  );
}

function FilterChip({ label, value, active, onClick, count }) {
  return (
    <button
      onClick={() => onClick(value)}
      data-testid={`filter-${value}`}
      className={`text-[10px] px-2.5 py-1 rounded-full border transition-colors ${
        active
          ? 'bg-[#C89B7B]/20 border-[#C89B7B]/50 text-[#E8C9A8]'
          : 'bg-white/[0.02] border-white/[0.08] text-white/50 hover:bg-white/[0.04]'
      }`}
    >
      {label}<span className="ml-1.5 text-white/30">{count}</span>
    </button>
  );
}

function RowFlagBadges({ m }) {
  const flags = [];
  if (!m.library_matched) flags.push({ label: 'No Lib', tone: 'bg-white/5 text-white/40 border-white/10', testid: 'flag-unmatched' });
  if ((m.confidence || 0) < 0.7) flags.push({ label: 'Low Conf', tone: 'bg-amber-900/30 text-amber-300 border-amber-700/30', testid: 'flag-lowconf' });
  if (m.source_range_invalid || m.needs_manual_range_review) flags.push({ label: 'Bad Range', tone: 'bg-red-900/30 text-red-300 border-red-700/30', testid: 'flag-badrange' });
  if (m.source_status_invalid) flags.push({ label: 'Bad Status', tone: 'bg-red-900/30 text-red-300 border-red-700/30', testid: 'flag-badstatus' });
  if (m.suspicious_value_unit) flags.push({ label: 'Unit?', tone: 'bg-orange-900/30 text-orange-300 border-orange-700/30', testid: 'flag-unit' });
  if (m.row_type === 'qualitative') flags.push({ label: 'Qual', tone: 'bg-cyan-900/30 text-cyan-300 border-cyan-700/30', testid: 'flag-qualitative' });
  if (m.row_type === 'manual_review') flags.push({ label: 'Review', tone: 'bg-amber-900/30 text-amber-300 border-amber-700/30', testid: 'flag-review' });
  if (m.row_type === 'ratio') flags.push({ label: 'Ratio', tone: 'bg-blue-900/30 text-blue-300 border-blue-700/30', testid: 'flag-ratio' });
  if (m.row_type === 'narrative') flags.push({ label: 'Narr', tone: 'bg-purple-900/30 text-purple-300 border-purple-700/30', testid: 'flag-narrative' });
  if (m.library_matched && flags.length === 0) {
    return <CheckCircle className="w-3 h-3 text-emerald-400/80" />;
  }
  return (
    <div className="flex flex-wrap gap-1">
      {flags.map((f) => (
        <span key={f.label} data-testid={f.testid}
          className={`text-[7px] uppercase tracking-wide px-1.5 py-[1px] rounded border ${f.tone}`}>
          {f.label}
        </span>
      ))}
    </div>
  );
}

// Row-level highlight for rows that need reviewer attention
function rowHighlightClass(m) {
  if (m.source_range_invalid || m.source_status_invalid) return 'bg-red-900/[0.04]';
  if ((m.confidence || 0) < 0.5) return 'bg-amber-900/[0.04]';
  if (!m.library_matched && m.row_type === 'numeric') return 'bg-white/[0.01]';
  return '';
}

function filteredMarkerRows(markers, mode, category, search) {
  const q = (search || '').trim().toLowerCase();
  const out = [];
  markers.forEach((m, idx) => {
    if (category && (m.category || '') !== category) return;
    if (q) {
      const hay = `${m.marker_name_raw || ''} ${m.marker_name_normalized || ''} ${m.name || ''}`.toLowerCase();
      if (!hay.includes(q)) return;
    }
    if (mode === 'unmatched' && m.library_matched) return;
    if (mode === 'low_conf' && (m.confidence || 0) >= 0.7) return;
    if (mode === 'invalid_range' && !(m.source_range_invalid || m.needs_manual_range_review)) return;
    if (mode === 'qualitative' && !['qualitative', 'manual_review', 'narrative'].includes(m.row_type)) return;
    if (mode === 'abnormal' && !['high', 'low', 'abnormal', 'critical'].includes(m.severity)) return;
    out.push({ m, idx });
  });
  return out;
}
